import {
  type AlphanumericArea,
  type BarcodeArea,
  withTemplateRegister,
} from '@kmns-internal/lobster-wasm'
import { useCallback, useState } from 'react'
import { type BBox, cropBBoxToCanvas } from './cropBBoxToCanvas'

export type ReadResult =
  | { success: true; value: string }
  | { success: false; error: string }

type AreaType = 'barcode' | 'text'

const AREA_ID = 'auto-detection'

const BASE_AREA = {
  id: AREA_ID,
  name: 'dummy',
  x: 0,
  y: 0,
  width: 1,
  height: 1,
}

const createArea = (type: AreaType): BarcodeArea | AlphanumericArea => {
  if (type === 'barcode') {
    return { ...BASE_AREA, type: 'barcode', value: '' }
  }
  /** value（正解値）に数字しか含まれていないと、WASM 側が 数字しか読み取りを行わないため、 英語を含めて、Alphanumeric モードで読み取る */
  return {
    ...BASE_AREA,
    type: 'alphanumeric',
    value: 'ALNUM_PLACEHOLDER',
  }
}

type UseAutoReaderOptions = {
  type: AreaType
}

const useAutoReader = ({ type }: UseAutoReaderOptions) => {
  const [isLoading, setIsLoading] = useState(false)

  const read = useCallback(
    async (imageUrl: string, bbox: BBox): Promise<ReadResult> => {
      setIsLoading(true)

      try {
        const canvas = await cropBBoxToCanvas(imageUrl, bbox)
        const area = createArea(type)

        const outerResult = await withTemplateRegister(
          { image: canvas, areas: [area] },
          async inspector => {
            return inspector.inspect({ image: canvas })
          },
        )

        if (!outerResult.ok) {
          return {
            success: false,
            error: `エラーが発生しました: ${outerResult.error.message}`,
          }
        }

        const inspectResult = outerResult.value
        if (!inspectResult.ok) {
          return {
            success: false,
            error: `エラーが発生しました: ${inspectResult.error.message}`,
          }
        }

        const areaResult = inspectResult.value.areas.find(a => a.id === AREA_ID)

        if (!areaResult?.detectedText) {
          return {
            success: false,
            error: '検出されませんでした',
          }
        }

        return {
          success: true,
          value: areaResult.detectedText,
        }
      } catch (error) {
        const message =
          error instanceof Error ? error.message : '不明なエラーが発生しました'
        return {
          success: false,
          error: message,
        }
      } finally {
        setIsLoading(false)
      }
    },
    [type],
  )

  return {
    read,
    isLoading,
  }
}

/**
 * バーコード読み取りフック
 */
export const useBarcodeReader = () => {
  const { read, isLoading } = useAutoReader({
    type: 'barcode',
  })

  return {
    readBarcode: read,
    isLoading,
  }
}

/**
 * テキスト読み取りフック
 */
export const useTextReader = () => {
  const { read, isLoading } = useAutoReader({
    type: 'text',
  })

  return {
    readText: read,
    isLoading,
  }
}
